import React, {
  Fragment,
  useContext,
  useEffect,
  useState,
  useMemo,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";

import { ThemeProvider } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";

import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import ToggleOnOutlinedIcon from "@mui/icons-material/ToggleOnOutlined";
import MedicalInformationOutlinedIcon from "@mui/icons-material/MedicalInformationOutlined";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import "../../../../assets/css/DataTableDemo.css";
import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "../../../../assets/css/PrimeGrid.css";

import { AlertContext } from "../../../../context/AlertContext";
import { AuthContext } from "../../../../context/AuthContext";
import { ConfiguracionContext } from "../../../../context/ConfiguracionContext";
import { getBrand, createBrandTheme } from "../../../../theme/brand";
import Spinner from "../../Spinner";
import Barra from "../../Barra";

export default function MedicosRecetas() {
  const [globalFilter, setGlobalFilter] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [confirmar, setConfirmar] = useState(null); // fila pendiente de confirmar
  const toast = useRef(null);
  const navigate = useNavigate();

  const { mensajeAlerta } = useContext(AlertContext);
  const { state, usuarioAutenticado } = useContext(AuthContext);
  const {
    statusAcceso,
    medicos,
    medicosRecetas,
    configuracion,
    obtenerMedicosRecetas,
    obtenerAcceso,
    activarMedico,
    obtenerMedicos,
    obtenerConfiguracion,
  } = useContext(ConfiguracionContext);

  const theme = createBrandTheme(getBrand(configuracion));

  useEffect(() => {
    usuarioAutenticado();
    obtenerMedicos({ todos: false });
    obtenerConfiguracion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (state.usuario) {
      obtenerAcceso({
        usuarioId: state.usuario.id,
        layout: "RecetasElectronicas",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  useEffect(() => {
    if (configuracion) {
      obtenerMedicosRecetas({ institucionId: configuracion.institucionId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configuracion]);

  useEffect(() => {
    if (statusAcceso === 400) {
      mensajeAlerta(
        "Accesos",
        "No tiene permiso para acceder a esta pantalla !.",
        "error"
      );
      navigate("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusAcceso]);

  // Lista de médicos del recetario que además existen en el sistema (match por recetarioId)
  const filas = useMemo(() => {
    if (!medicosRecetas || !medicos) return [];
    return medicosRecetas.filter((mr) =>
      medicos.some((m) => m.recetarioId === mr.id)
    );
  }, [medicosRecetas, medicos]);

  const activarClick = () => {
    if (!selectedRow) {
      toast.current.show({
        severity: "info",
        summary: "Médico",
        detail: "Debe seleccionar un médico.",
        life: 3000,
      });
      return;
    }
    setConfirmar(selectedRow);
  };

  const confirmarActivar = () => {
    activarMedico({ medico: confirmar });
    setConfirmar(null);
    setSelectedRow(null);
    setTimeout(() => {
      obtenerMedicosRecetas({ institucionId: configuracion.institucionId });
    }, 2000);
  };

  const exitClick = () => navigate("/");

  const activoTemplate = (rowData) => (
    <Fragment>
      <span className="p-column-title">Activo</span>
      <Chip
        size="small"
        label={rowData.active ? "Sí" : "No"}
        color={rowData.active ? "success" : "default"}
        variant={rowData.active ? "filled" : "outlined"}
      />
    </Fragment>
  );

  return (
    <Fragment>
      <Barra />
      <Toast ref={toast} />

      {statusAcceso === 0 || statusAcceso === 400 ? <Spinner /> : null}

      {statusAcceso === 200 ? (
        <ThemeProvider theme={theme}>
          <Box id="dataTableBotones" sx={{ px: { xs: 1, sm: 2 }, pb: 3 }}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", md: "center" }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexShrink: 0 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      display: "grid",
                      placeItems: "center",
                      bgcolor: "primary.main",
                      color: "primary.contrastText",
                    }}
                  >
                    <MedicalInformationOutlinedIcon />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                      Médicos Recetario
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {`${filas.length} médicos`}
                    </Typography>
                  </Box>
                </Stack>

                <Box sx={{ flexGrow: 1 }} />

                <TextField
                  size="small"
                  placeholder="Buscar…"
                  onInput={(e) => setGlobalFilter(e.target.value)}
                  sx={{ minWidth: { xs: "100%", md: 240 } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<ToggleOnOutlinedIcon />}
                  onClick={activarClick}
                  disabled={!selectedRow}
                >
                  Activar / Desactivar
                </Button>
                <Box sx={{ flexGrow: 1 }} />
                <Button
                  variant="text"
                  color="inherit"
                  startIcon={<CloseIcon />}
                  onClick={exitClick}
                >
                  Cerrar
                </Button>
              </Stack>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              {medicosRecetas ? (
                <DataTable
                  value={filas}
                  selection={selectedRow}
                  onSelectionChange={(e) => setSelectedRow(e.value)}
                  selectionMode="single"
                  dataKey="id"
                  className="p-datatable-sm p-datatable-striped p-datatable-responsive-demo mito-grid"
                  globalFilter={globalFilter}
                  emptyMessage="No hay datos."
                  paginator
                  paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                  currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords}"
                  rows={100}
                  rowsPerPageOptions={[10, 20, 50, 100]}
                >
                  <Column field="id" header="ID" sortable style={{ width: "8rem" }} />
                  <Column field="surname" header="Apellido" sortable />
                  <Column field="name" header="Nombre" sortable />
                  <Column field="address" header="Domicilio" sortable />
                  <Column field="workPhone" header="Teléfono" sortable />
                  <Column field="licenseNumber" header="Matrícula" sortable />
                  <Column field="licenseType" header="Tipo" sortable />
                  <Column field="active" header="Activo" body={activoTemplate} sortable style={{ width: "8rem" }} />
                </DataTable>
              ) : (
                <Spinner />
              )}
            </Paper>
          </Box>

          <Dialog open={Boolean(confirmar)} onClose={() => setConfirmar(null)} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontWeight: 700 }}>Confirmación</DialogTitle>
            <DialogContent>
              <DialogContentText>
                {confirmar && confirmar.active
                  ? "¿Confirma desactivar médico para recetas?"
                  : "¿Confirma activar médico para recetas?"}
              </DialogContentText>
              {confirmar ? (
                <DialogContentText sx={{ mt: 1, fontWeight: 600, color: "text.primary" }}>
                  {`Médico: ${confirmar.surname} ${confirmar.name}`}
                </DialogContentText>
              ) : null}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => setConfirmar(null)} color="inherit" startIcon={<CloseIcon />}>
                No
              </Button>
              <Button onClick={confirmarActivar} variant="contained" startIcon={<CheckIcon />}>
                Sí
              </Button>
            </DialogActions>
          </Dialog>
        </ThemeProvider>
      ) : null}
    </Fragment>
  );
}
